import { DBFactory } from "classes/database/db_factory";
import { BaseModel } from "../base.model";

type OrganizationSummaryConstructorParams = {
  organizationId: string;
  organizationName: string;
  classroomsMap?: {
    [key: string]: {
      classroomId: string;
      classroomName: string;
      userId: string;
    };
  };
  classroomIds: string[];
  packagesMap: {
    [key: string]: {
      packageId: string;
      packageName: string;
      purchasedQuantity: number;
      usedQuantity: number;
      subscriptionExpiryTimestamp?: number;
    };
  };
};

export class OrganizationSummary extends BaseModel {
  id: string;
  organizationId: string;
  organizationName: string;
  packagesMap: {
    [key: string]: {
      packageId: string;
      packageName: string;
      purchasedQuantity: number;
      usedQuantity: number;
      subscriptionExpiryTimestamp?: number;
    };
  };

  classroomsMap: {
    [key: string]: {
      classroomId: string;
      classroomName: string;
      userId: string;
    };
  };

  classroomIds: string[];

  constructor(params: OrganizationSummaryConstructorParams) {
    super();

    this.id = params.organizationId;
    this.organizationId = params.organizationId;
    this.organizationName = params.organizationName;
    this.packagesMap = params.packagesMap;
    this.classroomsMap = params.classroomsMap ?? {};
    this.classroomIds = params.classroomIds ?? [];
  }

  static override fromMap(map: any): OrganizationSummary {
    return new OrganizationSummary(map);
  }

  override toMap(): any {
    return {
      id: this.organizationId,
      organizationId: this.organizationId,
      organizationName: this.organizationName,
      packagesMap: this.packagesMap,
      classroomsMap: this.classroomsMap,
      classroomIds: this.classroomIds,
    };
  }

  override get databaseConfig() {
    return {
      path: `/organizationSummaries/${this.organizationId}`,
      collection: `/organizationSummaries`,
    };
  }

  isPackageActive(packageId: string): boolean {
    const packageInfo = this.packagesMap[packageId];

    if (!packageInfo) {
      return false;
    }

    if (
      packageInfo.subscriptionExpiryTimestamp &&
      packageInfo.subscriptionExpiryTimestamp < Date.now()
    ) {
      return false;
    }

    return packageInfo.purchasedQuantity >= packageInfo.usedQuantity;
  }

  get numClassroomLicensesPurchased(): number {
    return Object.values(this.packagesMap)
      .filter(p => p.packageName === "Classrooms" &&
        (!p.subscriptionExpiryTimestamp || p.subscriptionExpiryTimestamp > Date.now()))
      .reduce((total, p) => total + p.purchasedQuantity, 0);
  }

  get numClassroomsCreated(): number {
    if (this.classroomsMap == undefined) return 0;

    return Object.keys(this.classroomsMap).length;
  }

  get canAddClassroom(): boolean {
    return this.numClassroomLicensesPurchased > this.numClassroomsCreated;
  }

  async save() {
    const db = DBFactory.createDatabase();
    await db.save(this.toMap(), this.databaseConfig, false);
  }
}
